import React, { useEffect, useMemo, useState } from "react";
import { getChalets, getChaletById, saveReviews } from "../services/dataService";

type ApartmentListItem = { id: number; name: string };

type Review = {
  id: string;
  user: string;
  rating: number;
  comment: string;
  date: string; // YYYY-MM-DD
};


function todayYYYYMMDD() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function makeId() {
  // @ts-ignore
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeReviews(input: any): Review[] {
  if (!Array.isArray(input)) return [];
  return input.map((r) => ({
    id: String(r?.id ?? makeId()),
    user: String(r?.user ?? ""),
    rating: Number(r?.rating ?? 5),
    comment: String(r?.comment ?? ""),
    date: String(r?.date ?? todayYYYYMMDD()),
  }));
}

function validateReviews(reviews: Review[]) {
  const errors: Record<string, string[]> = {};
  reviews.forEach((r) => {
    const e: string[] = [];
    if (!r.user.trim()) e.push("Naam ontbreekt");
    if (!(r.rating >= 1 && r.rating <= 5)) e.push("Rating moet 1-5 zijn");
    if (!r.comment.trim()) e.push("Reviewtekst ontbreekt");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(r.date)) e.push("Datum moet YYYY-MM-DD zijn");
    if (e.length) errors[r.id] = e;
  });
  return errors;
}

// ---- API (using your list.php) ----
async function apiListApartments(): Promise<{ id: number; name: string }[]> {
  const chalets = await getChalets();

  return chalets.map((c: any) => ({
    id: Number(c.id),
    name: String(
      c.name ??          // ✅ most likely (from DB)
      c.title ??         // sometimes used in UI mapping
      c.chalet ??        // fallback
      c.apartmentName ?? // fallback
      `Chalet #${c.id}`  // final fallback
    ),
  }));
}

async function apiGetApartmentById(id: number) {
  const data = await getChaletById(id);

  return {
    id: Number(data?.id ?? id),
    name: String(data?.name ?? ""),
    reviews: normalizeReviews(data?.reviews),
  };
}

export function Reviews() {
  const [apartments, setApartments] = useState<ApartmentListItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | "">("");
  const [selectedName, setSelectedName] = useState("");

  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingOne, setLoadingOne] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error" | "info"; msg: string } | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingList(true);
        const rows = await apiListApartments();
        if (!alive) return;
        setApartments(rows);
      } catch (e: any) {
        setToast({ type: "error", msg: `Kon chalet-lijst niet laden: ${e?.message ?? "error"}` });
      } finally {
        setLoadingList(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (selectedId === "") return;
    let alive = true;

    (async () => {
      try {
        setLoadingOne(true);
        setDirty(false);
        const res = await apiGetApartmentById(Number(selectedId));
        if (!alive) return;
        setSelectedName(res.name);
        setReviews(res.reviews);
      } catch (e: any) {
        setToast({ type: "error", msg: `Kon reviews niet laden: ${e?.message ?? "error"}` });
        setReviews([]);
        setSelectedName("");
      } finally {
        setLoadingOne(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [selectedId]);

  const errorsById = useMemo(() => validateReviews(reviews), [reviews]);
  const hasErrors = useMemo(() => Object.keys(errorsById).length > 0, [errorsById]);

  function updateReview(id: string, patch: Partial<Review>) {
    setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  }

  function addReview() {
    setReviews((prev) => [
      {
        id: makeId(),
        user: "",
        rating: 5,
        comment: "",
        date: todayYYYYMMDD(),
      },
      ...prev,
    ]);
    setDirty(true);
  }

  function deleteReview(id: string) {
    setReviews((prev) => prev.filter((r) => r.id !== id));
    setDirty(true);
  }

  async function reloadFromServer() {
    if (selectedId === "") return;
    const res = await apiGetApartmentById(Number(selectedId));
    setSelectedName(res.name);
    setReviews(res.reviews);
    setDirty(false);
  }

  async function save() {
    if (selectedId === "") return;
    if (hasErrors) {
      setToast({ type: "error", msg: "Los eerst de errors op (rood gemarkeerd)." });
      return;
    }

    try {
      setSaving(true);
      const res = await saveReviews(Number(selectedId), reviews);

      if (res?.ok === false) {
        setToast({ type: "error", msg: res?.error ?? "Opslaan mislukt" });
        return;
      }

      // Optional: update from server response if it returns rows
      if (Array.isArray(res?.rows)) {
        setReviews(normalizeReviews(res.rows));
      }

      setDirty(false);
      setToast({ type: "success", msg: "Reviews opgeslagen ✅" });
    } catch (e: any) {
      setToast({ type: "error", msg: `Opslaan mislukt: ${e?.message ?? "error"}` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>Reviews beheren</h1>
          <p style={{ margin: "6px 0 0", opacity: 0.75 }}>Selecteer een chalet, bewerk reviews, klik op Opslaan.</p>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 800, opacity: 0.8 }}>Chalet</label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : "")}
              disabled={loadingList || saving}
              style={{ height: 38, borderRadius: 10, padding: "0 12px", border: "1px solid rgba(0,0,0,0.15)", minWidth: 320 }}
            >
              <option value="">{loadingList ? "Laden..." : "— Selecteer een chalet —"}</option>
              {apartments.map((a) => (
                <option key={a.id} value={a.id}>
                  #{a.id} — {a.name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={addReview}
            disabled={selectedId === "" || loadingOne || saving}
            style={{ height: 38, marginTop: 18, borderRadius: 10, padding: "0 14px", border: "1px solid rgba(0,0,0,0.15)", background: "white", fontWeight: 900 }}
          >
            + Review toevoegen
          </button>

          <button
            onClick={save}
            disabled={selectedId === "" || loadingOne || saving || hasErrors}
            style={{
              height: 38,
              marginTop: 18,
              borderRadius: 10,
              padding: "0 14px",
              border: "1px solid rgba(0,0,0,0.15)",
              background: hasErrors ? "rgba(0,0,0,0.06)" : "black",
              color: hasErrors ? "rgba(0,0,0,0.55)" : "white",
              fontWeight: 900,
              cursor: selectedId === "" || hasErrors ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Opslaan..." : "Opslaan"}
          </button>

          <button
            onClick={reloadFromServer}
            disabled={selectedId === "" || loadingOne || saving || !dirty}
            title="Herlaad van server (verliest niet-opgeslagen wijzigingen)"
            style={{
              height: 38,
              marginTop: 18,
              borderRadius: 10,
              padding: "0 14px",
              border: "1px solid rgba(0,0,0,0.15)",
              background: "white",
              fontWeight: 900,
              opacity: !dirty ? 0.6 : 1,
            }}
          >
            Herladen
          </button>
        </div>
      </div>

      {toast && (
        <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(0,0,0,0.12)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontWeight: 800 }}>{toast.msg}</div>
            <button onClick={() => setToast(null)} style={{ border: "none", background: "transparent", cursor: "pointer", fontWeight: 900, opacity: 0.6 }}>
              ✕
            </button>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14, opacity: 0.8 }}>
        {selectedId === "" ? (
          "Selecteer een chalet om reviews te beheren."
        ) : loadingOne ? (
          "Reviews laden…"
        ) : (
          <>
            Geselecteerd: <b>{selectedName || `#${selectedId}`}</b> — {reviews.length} review(s)
            {dirty ? <span style={{ marginLeft: 10, fontWeight: 900 }}>• Niet opgeslagen</span> : null}
          </>
        )}
      </div>

      <div style={{ marginTop: 16, display: "grid", gap: 14 }}>
        {selectedId !== "" && !loadingOne && reviews.length === 0 && (
          <div style={{ padding: 16, border: "1px dashed rgba(0,0,0,0.25)", borderRadius: 16, opacity: 0.75 }}>
            Geen reviews gevonden. Klik op <b>Review toevoegen</b>.
          </div>
        )}

        {reviews.map((r) => {
          const errs = errorsById[r.id] ?? [];
          const rowHasErrors = errs.length > 0;

          return (
            <div
              key={r.id}
              style={{
                padding: 14,
                borderRadius: 18,
                border: rowHasErrors ? "1px solid rgba(255,0,0,0.35)" : "1px solid rgba(0,0,0,0.12)",
                background: "white",
              }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px 170px 120px", gap: 10, alignItems: "end" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 900, opacity: 0.8 }}>Naam</label>
                  <input
                    value={r.user}
                    onChange={(e) => updateReview(r.id, { user: e.target.value })}
                    style={{ height: 36, borderRadius: 10, padding: "0 10px", border: "1px solid rgba(0,0,0,0.15)" }}
                    placeholder="Bijv. Sophie"
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 900, opacity: 0.8 }}>Rating</label>
                  <select
                    value={r.rating}
                    onChange={(e) => updateReview(r.id, { rating: Number(e.target.value) })}
                    style={{ height: 36, borderRadius: 10, padding: "0 10px", border: "1px solid rgba(0,0,0,0.15)" }}
                  >
                    {[5, 4, 3, 2, 1].map((v) => (
                      <option key={v} value={v}>
                        {v} ★
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 12, fontWeight: 900, opacity: 0.8 }}>Datum</label>
                  <input
                    type="date"
                    value={r.date}
                    onChange={(e) => updateReview(r.id, { date: e.target.value })}
                    style={{ height: 36, borderRadius: 10, padding: "0 10px", border: "1px solid rgba(0,0,0,0.15)" }}
                  />
                </div>

                <button
                  onClick={() => deleteReview(r.id)}
                  disabled={saving}
                  style={{
                    height: 36,
                    borderRadius: 10,
                    padding: "0 12px",
                    border: "1px solid rgba(255,0,0,0.25)",
                    background: "rgba(255,0,0,0.06)",
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  Verwijderen
                </button>
              </div>

              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 900, opacity: 0.8 }}>Review</label>
                <textarea
                  rows={4}
                  value={r.comment}
                  onChange={(e) => updateReview(r.id, { comment: e.target.value })}
                  style={{ width: "100%", borderRadius: 14, padding: 10, border: "1px solid rgba(0,0,0,0.15)", resize: "vertical" }}
                  placeholder="Schrijf hier de review..."
                />
              </div>

              {rowHasErrors && (
                <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 12, background: "rgba(255,0,0,0.06)" }}>
                  <div style={{ fontWeight: 900, marginBottom: 6 }}>Fix dit:</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {errs.map((m, i) => (
                      <li key={i} style={{ fontWeight: 800, opacity: 0.85 }}>
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}